// 開 Kinio KMS 簽章網址（sign.<plane 網域>）的視窗、送待簽內容、等結果（SPEC #kinio-kms-security「簽章網址」傳遞、TM-06、D35；k1-wbs M3-05）。
// 操作台只負責傳遞：要簽的東西由簽章頁自己從 payload 算、自己畫；回來的斷言 / 註冊結果原樣交給 kmgr / identity 驗。
//   簽章頁載入 → 對 opener 送 {type: "kinio-sign.ready"}；我們只認 origin = 簽章網址、source = 我們開的那個視窗
//   → 送 {type: "kinio-sign.request", id, kind, payload}（targetOrigin = 簽章網址）→ 等 {type: "kinio-sign.result", id, ok, result | error}

export type SignKind = 'register' | 'policy' | 'keyset';

export class SignError extends Error {
  constructor(public code: string) {
    super(code);
  }
}

export interface SignDeps {
  open: (url: string) => Window | null;
  addEventListener: (type: 'message', fn: (ev: MessageEvent) => void) => void;
  removeEventListener: (type: 'message', fn: (ev: MessageEvent) => void) => void;
  setInterval: (fn: () => void, ms: number) => number;
  clearInterval: (id: number) => void;
}

const browser: SignDeps = {
  open: (url) => window.open(url, 'kinio-sign', 'popup=yes,width=560,height=760'),
  addEventListener: (t, fn) => window.addEventListener(t, fn),
  removeEventListener: (t, fn) => window.removeEventListener(t, fn),
  setInterval: (fn, ms) => window.setInterval(fn, ms),
  clearInterval: (id) => window.clearInterval(id),
};

let seq = 0;

/** 開簽章視窗送一則請求。視窗被關掉 → SignError('closed')；使用者按取消 → SignError('canceled')；簽章頁判定請求不對 → SignError(碼)。 */
export function requestSignature<T = Record<string, unknown>>(signUrl: string, kind: SignKind, payload: unknown, deps: SignDeps = browser, timeoutMs = 10 * 60_000): Promise<T> {
  let origin: string;
  try {
    origin = new URL(signUrl).origin;
  } catch {
    return Promise.reject(new SignError('no_sign_url'));
  }
  if (!signUrl || !origin.startsWith('https://')) return Promise.reject(new SignError('no_sign_url'));
  const win = deps.open(signUrl);
  if (!win) return Promise.reject(new SignError('popup_blocked'));
  const id = `ks-${Date.now()}-${++seq}`;
  return new Promise<T>((resolve, reject) => {
    let sent = false;
    const started = Date.now();
    const done = (fn: () => void) => {
      deps.removeEventListener('message', onMsg);
      deps.clearInterval(timer);
      fn();
    };
    const onMsg = (ev: MessageEvent) => {
      if (ev.origin !== origin || ev.source !== win) return;
      const d = ev.data as { type?: string; id?: string; ok?: boolean; result?: T; error?: string } | null;
      if (!d) return;
      if (d.type === 'kinio-sign.ready' && !sent) {
        sent = true;
        win.postMessage({ type: 'kinio-sign.request', id, kind, payload }, origin);
        return;
      }
      if (d.type === 'kinio-sign.result' && d.id === id) {
        done(() => (d.ok && d.result ? resolve(d.result) : reject(new SignError(d.error || 'failed'))));
      }
    };
    const timer = deps.setInterval(() => {
      if (win.closed) done(() => reject(new SignError('closed')));
      else if (Date.now() - started > timeoutMs) done(() => reject(new SignError('timeout')));
    }, 500);
    deps.addEventListener('message', onMsg);
  });
}
